import { InlineEditorCancelButton, InlineEditorLogic, InlineEditorLogic_EditMode, InlineEditorLogic_ReadonlyMode, InlineEditorSaveButton, isEditMode, isReadonlyMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import { ButtonWidget, CompactInlineAlertWidget, InlineAlertType, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType, TextFieldWidget, TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { classes } from 'typestyle';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { wideField } from '~src/settings/general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { INPUT_WIDE_WIDTH, learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { SMTPAuthType} from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { cssClass } from '~src/style';
import { EmailValidationHelper } from '~src/userManagement/helpers/EmailValidationHelper';

import { EmailCustomizationFormFields, EmailCustomizationSettingsSectionProps } from './EmailCustomizationSettingsSection';
import { isValidDomain } from './helpers/domainVerification';
import { ISiteSMTPSettings, SMTPAuthTypeTransport } from './serverApi/requests/SetSiteSMTPCredentialTagRequest';
import { ExternalSMTPConfiguration } from './serverApi/SiteSMTPCredential';

export const SMTP_CREDENTIALS_SECTION = 'smtp-credentials-section';

export const READONLY_CREDENTIALS_SECTION = 'readonly-credentials-section';
export const READONLY_SENT_FROM_TEST_ID = 'readonly-sent-from';
export const READONLY_DOMAIN_TEST_ID = 'readonly-domain';
export const READONLY_SERVER_ADDRESS_TEST_ID = 'readonly-server-address';
export const READONLY_PORT_TEST_ID = 'readonly-port';
export const READONLY_AUTH_TYPE_TEST_ID = 'readonly-auth-type';

export const EDIT_SERVER_ADDRESS_FIELD_TEST_ID = 'email-customization-smtp-address';
export const EDIT_PORT_FIELD_TEST_ID = 'email-customization-port';
export const EDIT_USERNAME_FIELD_TEST_ID = 'email-customization-username';
export const EDIT_PASSWORD_FIELD_TEST_ID = 'email-customization-password';
export const EDIT_FRIENDLY_NAME_FIELD_TEST_ID = 'email-customization-friendly-name';
export const EDIT_DOMAIN_FIELD_TEST_ID = 'email-customization-domain';
export const EDIT_LOCALNAMEOREMAIL_FIELD_TEST_ID = 'email-customization-localnameoremail';
export const EDIT_TENANT_ID_FIELD_TEST_ID = 'email-customization-tenant-id';
export const EDIT_CLIENT_ID_FIELD_TEST_ID = 'email-customization-client-id';
export const EDIT_CLIENT_SECRET_FIELD_TEST_ID = 'email-customization-client-secret';
export const AUTH_TYPE_BASIC_TEST_ID = 'email-customization-auth-type-basic';
export const AUTH_TYPE_MICROSOFT_OAUTH_TEST_ID = 'email-customization-auth-type-microsoft-oauth';
export const SMTP_OAUTH_HELP_TEST_ID = 'email-customization-smtp-oauth-test-id';
export const SMTP_OAUTH_HELP_TOKEN = 'smtp-oauth';

export const CREDENTIALS_FORM_ERROR_TEST_ID = 'credentials-form-error';

export const MAX_GENERAL_INPUT_LENGTH = 100;
export const MAX_DOMAIN_INPUT_LENGTH = 253;
const cssPrefix = 'EmailCustomization';

export const MAX_PORT = 65535;

export const DEFAULT_SENDER_FRIENDLY_NAME = 'no-reply';
export const DEFAULT_SENDER_LOCAL_NAME = 'no-reply';
export const DEFAULT_SMTP_PORT = '587';

const emailCustomizationInputClass = cssClass(cssPrefix, 'credentials-form', {
  display: 'flex',
  flexDirection: 'column',
});

const enableOAuthCheckboxRowClass = cssClass(cssPrefix, 'enable-oauth-checkbox-row', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const credentialsFormButtonsClass = cssClass(cssPrefix, 'credentials-form-buttons', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const readonlyCredentialsClass = cssClass(cssPrefix, 'readonly-field', {
  paddingTop: 4,
  paddingBottom: 4,
  $nest: {
    '& span': {
      paddingRight: 4,
    }
  }
});

export interface CredentialsEditorState {
  host: string;
  port: string;
  authType: SMTPAuthType;
  username: string;
  friendlyName: string;
  password: string;
  domain: string;
  localNameOrEmail: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
}

export interface EmailCustomizationOptionsProps extends EmailCustomizationSettingsSectionProps {
  onSave: (values: EmailCustomizationFormFields) => void;
}

export function EmailCustomizationOptions(props: EmailCustomizationOptionsProps) {
  return <SMTPCredentialsSection {...props} />;
}

export function SMTPCredentialsSection(props: EmailCustomizationOptionsProps) {
  const emailCustomizationFormik = useFormikContext<EmailCustomizationFormFields>();
  const useEmailCustomization = emailCustomizationFormik.values.isCustomizationEnabled;
  const [credentialsErrorMessage, setCredentialsErrorMessage] = React.useState<string>(undefined);

  const handleSave = async (editorState: CredentialsEditorState): Promise<void> => {
    editorState.port ||= DEFAULT_SMTP_PORT;
    const isBasicAuth = editorState.authType === SMTPAuthType.BASIC;
    const emailCustomizationSettings: ISiteSMTPSettings = {
      custom_domain: editorState.domain.trim(),
      sender_friendly_name: editorState.friendlyName?.trim(),
      server_port: parseInt(editorState.port),
      sender_local_name: editorState.localNameOrEmail?.trim(),
      server_url: editorState.host.trim(),
      auth_type: isBasicAuth ? SMTPAuthTypeTransport.Basic : SMTPAuthTypeTransport.MsOAuth,
      // We track all the editorState values locally to allow users the ability to retain their values when
      // switching the AuthType radioButton, but we don't want to store the unused values. When they hit save
      // they are saving only the values that make sense for the selection (this will be enforced by the API,
      // but we also won't bother sending it up)
      server_username: !isBasicAuth ? undefined : editorState.username.trim(),
      server_password: !isBasicAuth ? undefined : editorState.password,
      tenant_id: isBasicAuth ? undefined : editorState.tenantId?.trim(),
      client_id: isBasicAuth ? undefined : editorState.clientId?.trim(),
      client_secret: isBasicAuth ? undefined : editorState.clientSecret?.trim(),
    };

    try {
      await ExternalSMTPConfiguration.setSiteSMTPCredential(emailCustomizationSettings, getSiteLuid());
      setCredentialsErrorMessage(undefined);
      saveFormHelper(editorState);

      ToasterService.getInstance().pop({
        type: ToastType.Success,
        text: Messages.emailServer_success(),
      });
    } catch (e) {
      setCredentialsErrorMessage(Messages.emailServer_errorSubmittingCredentials());
      throw e; // need to reject to stay in edit mode
    }
  };

  const saveFormHelper = (editorState: CredentialsEditorState) => {
    const values: EmailCustomizationFormFields = {
      isCustomizationEnabled: true,
      host: editorState.host,
      port: parseInt(editorState.port),
      authType: editorState.authType,
      username: editorState.username,
      friendlyName: editorState.friendlyName,
      domain: editorState.domain,
      password: editorState.password,
      localNameOrEmail: editorState.localNameOrEmail,
      tenantId: editorState.tenantId,
      clientId: editorState.clientId,
      clientSecret: editorState.clientSecret,
    };

    props.onSave(values);
    emailCustomizationFormik.resetForm({ values });
  };

  const canSave = (editorSavedState: CredentialsEditorState) => {
    const { host, port, authType, username, friendlyName, password, domain, localNameOrEmail, tenantId, clientId, clientSecret } = editorSavedState;

    const isValidHost = isHostValid(host);
    const isValidPort = isPortValid(port);
    const isValidFriendlyName = isFriendlyNameValid(friendlyName);
    const isValidPassword = isPasswordValid(password);
    const isValidDomain = isHostValid(domain);
    const isValidUsername = isUsernameValid(username);
    const isValidLocalName = isLocalNameOrEmailValid(localNameOrEmail);
    const isValidTenantId = isTenantIdValid(tenantId);
    const isValidClientId = isClientIdValid(clientId);
    const isValidClientSecret = isClientSecretValid(clientSecret);

    const isFormDirty = host !== editorStateOnEdit.host ||
      port !== editorStateOnEdit.port ||
      authType !== editorStateOnEdit.authType ||
      username !== editorStateOnEdit.username ||
      friendlyName !== editorStateOnEdit.friendlyName ||
      domain !== editorStateOnEdit.domain ||
      localNameOrEmail !== editorStateOnEdit.localNameOrEmail ||
      password !== editorStateOnEdit.password ||
      tenantId !== editorStateOnEdit.tenantId ||
      clientId !== editorStateOnEdit.clientId ||
      clientSecret !== editorStateOnEdit.clientSecret;

    const isRequiredFieldEmpty = authType === SMTPAuthType.BASIC
      ? (_.isEmpty(host) || _.isEmpty(domain) || _.isEmpty(username) || _.isEmpty(password))
      : (_.isEmpty(host) || _.isEmpty(domain) || _.isEmpty(tenantId) || _.isEmpty(clientId) || _.isEmpty(clientSecret));

    const isAuthSpecificValid = authType === SMTPAuthType.BASIC
      ? (isValidUsername && isValidPassword)
      : (isValidTenantId && isValidClientId && isValidClientSecret);

    return isValidHost && isValidPort && isValidFriendlyName && isValidDomain && isValidLocalName
      && isAuthSpecificValid
      && isFormDirty && !isRequiredFieldEmpty;
  };

  const credentials = emailCustomizationFormik.values;
  const editorStateOnEdit: CredentialsEditorState = {
    host: credentials.host,
    port: credentials.port + '',
    // if OAuth support is disabled - force BASIC
    authType: FeatureFlags.emailCustomizationOAuthSupportEnabled
      ? (credentials.authType ?? SMTPAuthType.BASIC) : SMTPAuthType.BASIC,
    username: '',
    localNameOrEmail: credentials.localNameOrEmail,
    friendlyName: credentials.friendlyName,
    domain: credentials.domain,
    password: '',
    tenantId: credentials.tenantId ?? '',
    clientId: credentials.clientId ?? '',
    clientSecret: '',
  };

  const handleCancel = () => {
    // if we don't already have credentials saved, canceling will go back to email customization off radio
    if (!credentials.host) {
      emailCustomizationFormik.setFieldValue('isCustomizationEnabled', false);
    }
  };

  const logic: InlineEditorLogic<CredentialsEditorState> = useInlineEditor<CredentialsEditorState>({
    editorStateWhenEnteringEditMode: editorStateOnEdit,
    canEdit: useEmailCustomization,
    onSave: handleSave,
    onCancel: handleCancel,
    canSaveFn: canSave,
  });

  React.useEffect(() => {
    if (!credentials.host && isReadonlyMode(logic) && useEmailCustomization) {
      logic.enterEditMode();
    }
  }, [credentials, logic, useEmailCustomization]);

  if (!useEmailCustomization) {
    return <></>;
  }

  return <div data-tb-test-id={SMTP_CREDENTIALS_SECTION}>
    {isEditMode(logic) ?
      <SMTPCredentialsEditMode editLogic={logic} credentialsErrorMessage={credentialsErrorMessage} /> :
      <SMTPCredentialsReadonlyMode
        readonlyLogic={logic}
        credentials={credentials}
      />}
  </div>;
}

const getSiteLuid = () => {
  return SessionSiteSelectors.siteLuid(getAppStore().getState());
};

const getLocalNameOrEmailDisplay = (localNameOrEmail: string, domain: string): string => {
  const localNameOrEmailDisplay = EmailValidationHelper.isValidEmail(localNameOrEmail)
    ? localNameOrEmail
    : `${localNameOrEmail}@${domain}`;
  return ` <${localNameOrEmailDisplay}>`;
};

interface SMTPCredentialsReadonlyModeProps {
  readonly readonlyLogic: InlineEditorLogic_ReadonlyMode;
  readonly credentials: EmailCustomizationFormFields;
}

export function SMTPCredentialsReadonlyMode(props: SMTPCredentialsReadonlyModeProps) {
  const { readonlyLogic, credentials } = props;

  const hasCredentials = !!credentials.host;
  const localNameOrEmail = credentials.localNameOrEmail || DEFAULT_SENDER_LOCAL_NAME;
  const authTypeLabel = credentials.authType === SMTPAuthType.MS_OAUTH
    ? Messages.emailServer_authType_msOauth()
    : Messages.emailServer_authType_basic();

  const credentialsSection = <div className={readonlyCredentialsClass} data-tb-test-id={READONLY_CREDENTIALS_SECTION}>
    <div data-tb-test-id={READONLY_SENT_FROM_TEST_ID}><span style={{ fontWeight: 'bold' }}>{Messages.emailServer_emailSentFrom()}</span><span>{(credentials.friendlyName || DEFAULT_SENDER_FRIENDLY_NAME) + getLocalNameOrEmailDisplay(localNameOrEmail, credentials.domain)}</span></div>
    <div data-tb-test-id={READONLY_DOMAIN_TEST_ID}><span style={{ fontWeight: 'bold' }}>{Messages.emailServer_domain_colon()}</span><span>{credentials.domain}</span></div>
    <div data-tb-test-id={READONLY_SERVER_ADDRESS_TEST_ID}><span>{Messages.emailServer_smtpServerAddress_colon()}</span><span>{credentials.host}</span></div>
    {FeatureFlags.emailCustomizationOAuthSupportEnabled && (
      <div data-tb-test-id={READONLY_AUTH_TYPE_TEST_ID}><span>{Messages.emailServer_authentication_colon()}</span><span>{authTypeLabel}</span></div>
    )}
    <div data-tb-test-id={READONLY_PORT_TEST_ID}><span>{Messages.emailServer_portNumber_colon()}</span><span>{credentials.port}</span></div>
  </div>;

  return (
    <>
      {hasCredentials && credentialsSection}
      <div style={{ paddingTop: 4 }}>
        <ButtonWidget
          style={readonlyLogic.canEdit ? undefined : { visibility: 'hidden' }}
          disabled={!readonlyLogic.canEdit}
          onClick={() => readonlyLogic.enterEditMode()}
          children={hasCredentials ? Messages.edit() : Messages.emailServer_addButton()}
          testId={'edit-server-credentials-button'}
        />
      </div>
    </>
  );
}

interface SMTPCredentialsEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<CredentialsEditorState>;
  readonly credentialsErrorMessage?: string;
}

export function SMTPCredentialsEditMode(props: SMTPCredentialsEditModeProps) {
  const { editLogic, credentialsErrorMessage } = props;
  const [hasVisited, setHasVisited] = React.useState({
    host: false,
    port: false,
    username: false,
    password: false,
    domain: false,
    tenantId: false,
    clientId: false,
    clientSecret: false,
  });

  const AUTH_TYPE_OPTIONS: RadioButtonWithLabelOption<SMTPAuthType>[] = [
    { value: SMTPAuthType.BASIC,  label: Messages.emailServer_authType_basic(), testId: AUTH_TYPE_BASIC_TEST_ID },
    {
      value: SMTPAuthType.MS_OAUTH,
      label:
        <span>
          {Messages.emailServer_authType_msOauth()}
          <TextLinkWidget
            testId={SMTP_OAUTH_HELP_TEST_ID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory(SMTP_OAUTH_HELP_TOKEN)}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </span>,
      testId: AUTH_TYPE_MICROSOFT_OAUTH_TEST_ID
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter' && editLogic.canSaveThisEditorState) {
      editLogic.startSave();
    }
  };

  const truncateText = (input: string, maxLength: number) => {
    return input.substring(0, maxLength);
  };

  const renderAuthRadioButtons = () => {
    if (FeatureFlags.emailCustomizationOAuthSupportEnabled) {
      return (
        <>
          <div style={{paddingTop: 4}} className={enableOAuthCheckboxRowClass}>
            <RadioButtonWithLabelGroupWidget
              label={Messages.emailServer_authType()}
              buttonList={AUTH_TYPE_OPTIONS}
              checkedIndex={AUTH_TYPE_OPTIONS.findIndex(opt => opt.value === editLogic.editorState.authType)}
              handleChange={(value: SMTPAuthType) => editLogic.setEditorStateWithinEditMode({authType: value})}
              layout={RadioLayoutType.Horizontal}
            />
          </div>
        </>
      );
    }
  };

  const renderAuthFields = () => {
    switch (editLogic.editorState.authType) {
      case SMTPAuthType.BASIC:
        return (
          <>
            <TextFieldWidget
              label={Messages.emailServer_username()}
              onBlur={() => setHasVisited(prev => ({ ...prev, username: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                  username: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)});
                setHasVisited(prev => ({ ...prev, username: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ username: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_USERNAME_FIELD_TEST_ID}
              valid={hasVisited.username && !isUsernameValid(editLogic.editorState.username) ? false : undefined}
              value={editLogic.editorState.username}
            />
            <TextFieldWidget
              label={Messages.password()}
              onBlur={() => setHasVisited(prev => ({ ...prev, password: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                  password: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)});
                setHasVisited(prev => ({ ...prev, password: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ password: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_PASSWORD_FIELD_TEST_ID}
              type='password'
              valid={hasVisited.password && !isPasswordValid(editLogic.editorState.password) ? false : undefined}
              value={editLogic.editorState.password}
            />
          </>
        );
      case SMTPAuthType.MS_OAUTH:
        return (
          <>
            <TextFieldWidget
              label={Messages.emailServer_msOauth_tenant()}
              onBlur={() => setHasVisited(prev => ({ ...prev, tenantId: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                  tenantId: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH),
                });
                setHasVisited(prev => ({ ...prev, tenantId: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ tenantId: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_TENANT_ID_FIELD_TEST_ID}
              valid={hasVisited.tenantId && !isTenantIdValid(editLogic.editorState.tenantId) ? false : undefined}
              value={editLogic.editorState.tenantId}
            />
            <TextFieldWidget
              label={Messages.emailServer_msOauth_clientId()}
              onBlur={() => setHasVisited(prev => ({ ...prev, clientId: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                  clientId: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH),
                });
                setHasVisited(prev => ({ ...prev, clientId: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ clientId: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_CLIENT_ID_FIELD_TEST_ID}
              valid={hasVisited.clientId && !isClientIdValid(editLogic.editorState.clientId) ? false : undefined}
              value={editLogic.editorState.clientId}
            />
            <TextFieldWidget
              label={Messages.emailServer_msOauth_clientSecret()}
              onBlur={() => setHasVisited(prev => ({ ...prev, clientSecret: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                  clientSecret: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH),
                });
                setHasVisited(prev => ({ ...prev, clientSecret: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ clientSecret: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_CLIENT_SECRET_FIELD_TEST_ID}
              type='password'
              valid={hasVisited.clientSecret && !isClientSecretValid(editLogic.editorState.clientSecret) ? false : undefined}
              value={editLogic.editorState.clientSecret}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div style={{ width: INPUT_WIDE_WIDTH }}>
        <div style={{ paddingBottom: 4 }}>
          <div className={classes(emailCustomizationInputClass, wideField)}>
            <TextFieldWidget
              label={Messages.emailServer_smtpServerAddress()}
              onBlur={() => setHasVisited(prev => ({ ...prev, host: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                host: truncateText(e.target.value, MAX_DOMAIN_INPUT_LENGTH)});
                setHasVisited(prev => ({ ...prev, host: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ host: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_SERVER_ADDRESS_FIELD_TEST_ID}
              valid={hasVisited.host && !isHostValid(editLogic.editorState.host) ? false : undefined}
              value={editLogic.editorState.host}
            />
            <TextFieldWidget
              label={Messages.emailServer_portNumber()}
              onBlur={() => setHasVisited(prev => ({ ...prev, port: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                port: truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)});
                setHasVisited(prev => ({ ...prev, port: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ port: '' })}
              onKeyDown={handleKeyDown}
              placeholder={Messages.emailServer_portPlaceholder()}
              testId={EDIT_PORT_FIELD_TEST_ID}
              valid={hasVisited.port && !isPortValid(editLogic.editorState.port) ? false : undefined}
              value={editLogic.editorState.port}
            />
          </div>
          {renderAuthRadioButtons()}
          <div className={classes(emailCustomizationInputClass, wideField)}>
            {renderAuthFields()}
            <TextFieldWidget
              label={Messages.emailServer_domain()}
              onBlur={() => setHasVisited(prev => ({ ...prev, domain: true }))}
              onChange={e => {
                editLogic.setEditorStateWithinEditMode({
                domain: truncateText(e.target.value, MAX_DOMAIN_INPUT_LENGTH)});
                setHasVisited(prev => ({ ...prev, domain: true }));
              }}
              onClear={() => editLogic.setEditorStateWithinEditMode({ domain: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_DOMAIN_FIELD_TEST_ID}
              valid={hasVisited.domain && !isHostValid(editLogic.editorState.domain) ? false : undefined}
              value={editLogic.editorState.domain}
            />
            <TextFieldWidget
              label={Messages.emailServer_friendlyNameFieldLabel()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                friendlyName:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ friendlyName: '' })}
              onKeyDown={handleKeyDown}
              testId={EDIT_FRIENDLY_NAME_FIELD_TEST_ID}
              valid={!isFriendlyNameValid(editLogic.editorState.friendlyName) ? false : undefined}
              value={editLogic.editorState.friendlyName}
            />
            <TextFieldWidget
              label={Messages.emailServer_localName()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                localNameOrEmail:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ localNameOrEmail: '' })}
              onKeyDown={handleKeyDown}
              testId={EDIT_LOCALNAMEOREMAIL_FIELD_TEST_ID}
              valid={!isLocalNameOrEmailValid(editLogic.editorState.localNameOrEmail) ? false : undefined}
              value={editLogic.editorState.localNameOrEmail}
            />
          </div>
        </div>
        {credentialsErrorMessage && <CompactInlineAlertWidget
          children={<span>{credentialsErrorMessage}</span>}
          style={{ marginTop: 4, marginBottom: 4 }}
          type={InlineAlertType.Error}
          testId={CREDENTIALS_FORM_ERROR_TEST_ID}
        />}
        <div style={{ paddingTop: 4 }} className={credentialsFormButtonsClass}>
          <InlineEditorCancelButton {...editLogic} />
          <span style={{ paddingLeft: 8 }}>
            <InlineEditorSaveButton {...editLogic} />
          </span>
        </div>
      </div>
    </>
  );
}

function isStringValid(input: string, maxLength: number): boolean {
  return !_.isUndefined(input) && input.trim().length > 0 && input.length <= maxLength;
}

function isHostValid(host: string): boolean {
  return (isStringValid(host, MAX_DOMAIN_INPUT_LENGTH) && isValidDomain(host));
}

function isPortValid(port: string): boolean {
  // undefined/empty/blank are treated as valid
  if (!port) { return true; }

  // non-digit strings are invalid
  if (!(/^\d*$/.exec(port))) { return false; }

  const portNumber = parseInt(port, 10);

  return portNumber > 0 && portNumber <= MAX_PORT;
}

function isUsernameValid(username: string): boolean {
  return isStringValid(username, MAX_GENERAL_INPUT_LENGTH);
}

function isLocalNameOrEmailValid(localNameOrEmail: string): boolean {
  /* The following regex checks whether the provided local name is a valid local name. Criteria is:
    alphanumeric, with some special characters, period as long as it's not first
    nor last nor consecutive with another period, and quotes strings allow additional
    characters but must start and end with double quotes (RFC 5321, RFC 5322).
    Examples of valid local names: simple, simple.extra, hello-simple+name,
    "quoted.@,spaces", user@[IPv6:2001:db8::1] */
  const localNameRegex = new RegExp('^[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+' +
    '(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*$|' +
    '^\"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|' +
    '\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+\"$');

  // localNameOrEmail must either be null or empty (and it'll default to no-reply) or it needs to pass
  // our MAX_GENERAL_INPUT_LENGTH test and either pass the local name RegEx or be a valid email address.
  return !localNameOrEmail || (isStringValid(localNameOrEmail, MAX_GENERAL_INPUT_LENGTH) &&
    (localNameRegex.test(localNameOrEmail) || EmailValidationHelper.isValidEmail(localNameOrEmail)));
}

function isGuidValid(guid: string): boolean {
  if (!guid) return false;

  const guidRegex = /^\{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\}?$/i;
  //
  return guidRegex.test(guid);
}

function isPasswordValid(password: string): boolean {
  return isStringValid(password, MAX_GENERAL_INPUT_LENGTH);
}

function isFriendlyNameValid(friendlyName: string): boolean {
  return !friendlyName || isStringValid(friendlyName, MAX_GENERAL_INPUT_LENGTH);
}

function isTenantIdValid(tenantId: string): boolean {
  return isStringValid(tenantId, MAX_GENERAL_INPUT_LENGTH) && (isGuidValid(tenantId) || isValidDomain(tenantId));
}

function isClientIdValid(clientId: string): boolean {
  return isGuidValid(clientId);
}

function isClientSecretValid(clientSecret: string): boolean {
  return isStringValid(clientSecret, MAX_GENERAL_INPUT_LENGTH);
}